/**
 * Auth adapters — one per credential kind in design §2.4.
 *
 * An adapter's whole job is to turn "who am I" into request headers (plus, for
 * cookies, a `credentials` mode). It never touches the transport, so swapping a
 * web client to a device credential is a one-line change at construction.
 *
 * The device adapters (`apiKeyAuth`, and `bearerAuth` when used from the recorder
 * or extension) are STUBS in exactly one respect, and it is called out in each:
 * design §2.4 requires a device-bound DPoP-style proof (ECDSA P-256, `htm`/`htu`/
 * `iat`/`jti`/`ath`) alongside the credential. `signProof` is the seam that lands;
 * until it is supplied, these send the bearer credential alone.
 */

export type AuthHeaders = Readonly<Record<string, string>>;

/**
 * What the transport needs from a credential for one request.
 *
 * `method` and `url` are passed so a DPoP implementation can bind the proof to
 * them (`htm` / `htu`); the current adapters ignore them.
 */
export type AuthRequestInfo = {
  readonly method: string;
  readonly url: string;
};

/**
 * The `fetch` credentials mode.
 *
 * Spelled out rather than using the DOM's `RequestCredentials`: this package is
 * source-only, so a consumer type-checks THIS file with ITS OWN `lib` setting.
 * A Node consumer (`apps/api`'s tests) has no DOM lib, and a DOM-only type name
 * here makes the whole package uncompilable for them. The union is identical to
 * the DOM type's definition.
 */
export type CredentialsMode = 'omit' | 'same-origin' | 'include';

export type CrmAuth = {
  /** Discriminator, for logs and for `onAuthError` consumers. */
  readonly kind: 'cookie' | 'bearer' | 'apiKey' | 'none';
  /** Headers to merge into the request. May be async (token refresh). */
  headers(info: AuthRequestInfo): Promise<AuthHeaders> | AuthHeaders;
  /**
   * `fetch` credentials mode. Only the cookie adapter sets it; browsers need
   * `include` for a cross-origin session cookie, and it is inert under Node.
   */
  readonly credentials?: CredentialsMode;
  /**
   * DPoP proof signer, surfaced to the TRANSPORT rather than consumed inside
   * `headers()`. The distinction is load-bearing: `headers()` runs at a layer
   * that only knows a procedure path and guesses `POST`, while the server
   * (`apps/api/src/dpop.ts`) verifies `htm`/`htu` against the request actually
   * on the wire. The client's `fetch` handler is the one place the real method
   * and URL exist, so that is where the proof is minted and attached — a proof
   * produced from `headers()`'s placeholder info would be rejected on every
   * GET and on every URL with a query string.
   */
  readonly signProof?: (info: AuthRequestInfo) => Promise<string | null> | string | null;
};

const NO_HEADERS: AuthHeaders = Object.freeze({});

/**
 * Web. The better-auth session cookie rides along with the request; nothing goes
 * in a header. Requires the API's `trustedOrigins` to include the web origin and
 * the cookie to be `SameSite=Lax` (design §2.4).
 */
export function cookieAuth(): CrmAuth {
  return {
    kind: 'cookie',
    credentials: 'include',
    headers: () => NO_HEADERS,
  };
}

export type BearerAuthOptions = {
  /**
   * Returns the current JWT. Called on EVERY request (including each retry), so
   * implement refresh-if-expiring here — that is the only hook that can.
   * Returning null sends the request unauthenticated, which the server answers
   * with 401 and `onAuthError`.
   */
  getToken(): Promise<string | null> | string | null;
  /** DPoP proof signer (design §2.4). Unset = no proof header. */
  signProof?: (info: AuthRequestInfo) => Promise<string | null> | string | null;
};

/**
 * Mobile (Expo). Short-lived JWT from better-auth's `jwt` plugin, verified by the
 * API against its own JWKS. The token lives in `expo-secure-store`; this adapter
 * only ever reads it through `getToken`.
 */
export function bearerAuth(options: BearerAuthOptions): CrmAuth {
  return {
    kind: 'bearer',
    async headers() {
      const token = await options.getToken();
      if (token === null || token === undefined || token === '') {
        return NO_HEADERS;
      }
      // The proof is NOT minted here: `headers()` sees placeholder request
      // info, and a DPoP proof must bind to the real method/URL. The transport
      // consults `signProof` once the request is built.
      return { authorization: `Bearer ${token}` };
    },
    ...(options.signProof === undefined ? {} : { signProof: options.signProof }),
  };
}

export type ApiKeyAuthOptions = {
  /** The better-auth API key issued to this device at pairing time. */
  getApiKey(): Promise<string | null> | string | null;
  /**
   * DPoP proof over (`htm`, `htu`, `iat`, `jti`, `ath`), signed by the device key
   * (Secure Enclave on macOS, non-extractable WebCrypto in IndexedDB for the
   * extension). **Until this is supplied the credential stands alone**, which is
   * explicitly weaker than design §2.4 requires for a shipped device client.
   */
  signProof?: (info: AuthRequestInfo) => Promise<string | null> | string | null;
};

/**
 * macOS recorder + Chrome extension. `x-api-key` is the header name configured on
 * the server's `apiKey()` plugin — the two must agree, so do not rename it here
 * without changing `packages/auth`.
 */
export function apiKeyAuth(options: ApiKeyAuthOptions): CrmAuth {
  return {
    kind: 'apiKey',
    async headers() {
      const key = await options.getApiKey();
      if (key === null || key === undefined || key === '') {
        return NO_HEADERS;
      }
      // Proof minting lives in the transport (see `CrmAuth.signProof`): a proof
      // bound to `headers()`'s placeholder method/URL fails the server's
      // `htm`/`htu` verification on any GET or query-carrying request.
      return { 'x-api-key': key };
    },
    ...(options.signProof === undefined ? {} : { signProof: options.signProof }),
  };
}

/** No credential at all. Only `health.ping` is reachable; useful in tests. */
export function noAuth(): CrmAuth {
  return { kind: 'none', headers: () => NO_HEADERS };
}
